/**
 * FinancesTab - Admin Financial Overview
 *
 * Displays all payments from all users with summary totals.
 * Allows admin to see the complete financial picture.
 */

import { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Card } from '../../components/ui';
import api from '../../services/api';

interface Payment {
  id: string;
  userId: string;
  userEmail: string;
  orderId: string;
  captureId: string | null;
  subscriptionId: string | null;
  planId: string | null;
  amount: number;
  currency: string;
  paymentType: string;
  paymentTypeLabel: string;
  status: string;
  statusLabel: string;
  createdAt: string;
}

interface PaymentsSummary {
  totalPayments: number;
  totalAmount: number;
  completedAmount: number;
  completedCount: number;
  pendingCount: number;
  refundedCount: number;
  currency: string;
}

interface PaginatedPayments {
  payments: Payment[];
  pagination: {
    page: number;
    pageSize: number;
    total: number;
    totalPages: number;
  };
  summary: PaymentsSummary;
}

interface ApiResponse<T> {
  success: boolean;
  data?: T;
  error?: { code: string; message: string };
}

export function FinancesTab() {
  const { t } = useTranslation();
  const [payments, setPayments] = useState<Payment[]>([]);
  const [summary, setSummary] = useState<PaymentsSummary | null>(null);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [pageSize] = useState(20);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const loadPayments = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams();
      params.append('page', page.toString());
      params.append('pageSize', pageSize.toString());

      const response = await api.get<ApiResponse<PaginatedPayments>>(`/admin/payments?${params}`);
      if (response.data.success && response.data.data) {
        setPayments(response.data.data.payments);
        setTotal(response.data.data.pagination.total);
        setTotalPages(response.data.data.pagination.totalPages);
        setSummary(response.data.data.summary);
      } else {
        setError(response.data.error?.message || t('admin.finances.loadError'));
      }
    } catch {
      setError(t('admin.finances.loadError'));
    } finally {
      setIsLoading(false);
    }
  }, [page, pageSize, t]);

  useEffect(() => {
    loadPayments();
  }, [loadPayments]);

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('fr-FR', {
      style: 'currency',
      currency: 'EUR',
    }).format(amount);
  };

  if (isLoading && payments.length === 0) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-violet-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Error message */}
      {error && (
        <div className="p-4 bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 rounded-lg text-red-700 dark:text-red-400">
          {error}
          <button onClick={() => setError(null)} className="ml-2 underline">
            {t('common.close')}
          </button>
        </div>
      )}

      {/* Summary Cards */}
      {summary && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Card className="p-4">
            <div className="text-sm text-slate-600 dark:text-slate-400">
              {t('admin.finances.summary.totalRevenue')}
            </div>
            <div className="text-2xl font-bold text-green-600 dark:text-green-400 mt-1">
              {formatCurrency(summary.completedAmount)}
            </div>
          </Card>
          <Card className="p-4">
            <div className="text-sm text-slate-600 dark:text-slate-400">
              {t('admin.finances.summary.completedPayments')}
            </div>
            <div className="text-2xl font-bold text-slate-900 dark:text-slate-100 mt-1">
              {summary.completedCount}
            </div>
          </Card>
          <Card className="p-4">
            <div className="text-sm text-slate-600 dark:text-slate-400">
              {t('admin.finances.summary.pendingPayments')}
            </div>
            <div className="text-2xl font-bold text-yellow-600 dark:text-yellow-400 mt-1">
              {summary.pendingCount}
            </div>
          </Card>
          <Card className="p-4">
            <div className="text-sm text-slate-600 dark:text-slate-400">
              {t('admin.finances.summary.refundedPayments')}
            </div>
            <div className="text-2xl font-bold text-red-600 dark:text-red-400 mt-1">
              {summary.refundedCount}
            </div>
          </Card>
        </div>
      )}

      {/* Payments Table */}
      <Card>
        <div className="p-4 border-b border-slate-200 dark:border-slate-700">
          <h3 className="text-lg font-semibold text-slate-900 dark:text-slate-100">
            {t('admin.finances.paymentsTitle')}
          </h3>
          <p className="text-sm text-slate-600 dark:text-slate-400 mt-1">
            {t('admin.finances.paymentsSubtitle', { count: total })}
          </p>
        </div>

        {/* Mobile View */}
        <div className="md:hidden divide-y divide-slate-200 dark:divide-slate-700">
          {payments.length === 0 ? (
            <div className="p-8 text-center text-slate-500 dark:text-slate-400">
              {t('admin.finances.noPayments')}
            </div>
          ) : (
            payments.map((payment) => (
              <div key={payment.id} className="p-4 space-y-2">
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium text-slate-900 dark:text-slate-100">
                    {formatDate(payment.createdAt)}
                  </span>
                  <span className={`inline-flex px-2 py-0.5 text-xs font-medium rounded-full ${
                    payment.status === 'completed' 
                      ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400'
                      : payment.status === 'pending'
                      ? 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400'
                      : 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400'
                  }`}>
                    {payment.statusLabel}
                  </span>
                </div>
                <div className="text-sm text-slate-600 dark:text-slate-400 truncate">
                  {payment.userEmail}
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-slate-500 dark:text-slate-400">
                    {payment.paymentTypeLabel}
                  </span>
                  <span className="text-lg font-semibold text-slate-900 dark:text-slate-100">
                    {formatCurrency(payment.amount)}
                  </span>
                </div>
                <div className="text-xs text-slate-400 truncate" title={payment.orderId}>
                  Réf: {payment.orderId}
                </div>
              </div>
            ))
          )}
        </div>

        {/* Desktop Table */}
        <div className="hidden md:block overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="bg-slate-50 dark:bg-slate-800/50">
                <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wider">
                  {t('admin.finances.table.date')}
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wider">
                  {t('admin.finances.table.user')}
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wider">
                  {t('admin.finances.table.type')}
                </th>
                <th className="px-4 py-3 text-right text-xs font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wider">
                  {t('admin.finances.table.amount')}
                </th>
                <th className="px-4 py-3 text-center text-xs font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wider">
                  {t('admin.finances.table.status')}
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wider">
                  {t('admin.finances.table.reference')}
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-200 dark:divide-slate-700">
              {payments.length === 0 ? (
                <tr>
                  <td colSpan={6} className="px-4 py-8 text-center text-slate-500 dark:text-slate-400">
                    {t('admin.finances.noPayments')}
                  </td>
                </tr>
              ) : (
                payments.map((payment) => (
                  <tr key={payment.id} className="hover:bg-slate-50 dark:hover:bg-slate-800/50">
                    <td className="px-4 py-3 text-sm text-slate-900 dark:text-slate-100 whitespace-nowrap">
                      {formatDate(payment.createdAt)}
                    </td>
                    <td className="px-4 py-3 text-sm text-slate-600 dark:text-slate-400">
                      {payment.userEmail}
                    </td>
                    <td className="px-4 py-3 text-sm text-slate-600 dark:text-slate-400">
                      {payment.paymentTypeLabel}
                    </td>
                    <td className="px-4 py-3 text-sm text-right font-medium text-slate-900 dark:text-slate-100">
                      {formatCurrency(payment.amount)}
                    </td>
                    <td className="px-4 py-3 text-center">
                      <span className={`inline-flex px-2 py-0.5 text-xs font-medium rounded-full ${
                        payment.status === 'completed' 
                          ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400'
                          : payment.status === 'pending'
                          ? 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400'
                          : 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400'
                      }`}>
                        {payment.statusLabel}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-xs text-slate-500 dark:text-slate-400 font-mono truncate max-w-[150px]" title={payment.orderId}>
                      {payment.orderId}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="p-4 border-t border-slate-200 dark:border-slate-700 flex flex-col sm:flex-row items-center justify-between gap-4">
            <p className="text-sm text-slate-600 dark:text-slate-400 text-center sm:text-left">
              {t('admin.finances.showing', { 
                from: (page - 1) * pageSize + 1, 
                to: Math.min(page * pageSize, total), 
                total 
              })}
            </p>
            <div className="flex items-center gap-2">
              <Button
                variant="secondary"
                size="sm"
                onClick={() => setPage(p => Math.max(1, p - 1))}
                disabled={page === 1}
              >
                {t('common.previous')}
              </Button>
              <span className="flex items-center px-3 text-sm text-slate-600">
                {page} / {totalPages}
              </span>
              <Button
                variant="secondary"
                size="sm"
                onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                disabled={page === totalPages}
              >
                {t('common.next')}
              </Button>
            </div>
          </div>
        )}
      </Card>
    </div>
  );
}

export default FinancesTab;
